<script setup>
import TabPill from "@/components/global-themed/tabs/TabPill.vue";
import {computed, ref} from "vue";
import AccordionTransition from "@/components/transitions/AccordionTransition.vue";
import HideActions from "@/components/actions/buttons/HideActions.vue";
import CompleteActionButton from "@/components/actions/buttons/CompleteActionButton.vue";
import GenerateFixButton from "@/components/actions/buttons/GenerateFixButton.vue";
import {fullUrl} from "@/customValidation";
import DefaultUrlInput from "@/components/global-themed/inputs/DefaultUrlInput.vue";
import useUser from "@/composables/useUser";

const props = defineProps({
  action: {
    required: true,
    type: Object,
  }
});

const {isAdmin} = useUser();

const tab = ref('what');
const inputValue = ref('');
const inputComponent = ref(null);

const action = computed(() => props.action);

const content = computed(() => {
  if (tab.value === 'what') {
    return action.value.description;
  }

  if (tab.value === 'why') {
    return action.value.why_important;
  }

  return action.value.how_to_fix;
});

const disableComplete = computed(() => {
  if (!action.value.input_label) {
    return false;
  }

  return !!inputComponent.value?.errorMessage;
});

const isManualAction = computed(() => action.value.category === 'Manual');
</script>

<template>
  <AccordionTransition
    class="tw-border-t tw-bg-white tw-transition-all tw-break-words"
  >
    <div>
      <div
        v-if="!isManualAction"
        class="tw-flex tw-gap-2 tw-p-8"
      >
        <TabPill
          v-if="!!action.description"
          :active="tab === 'what'"
          @click="tab = 'what'"
        >
          What you need to do
        </TabPill>
        <TabPill
          v-if="action.why_important"
          :active="tab === 'why'"
          @click="tab = 'why'"
        >
          Why is it important?
        </TabPill>
        <TabPill
          v-if="action.how_to_fix"
          :active="tab === 'how'"
          @click="tab = 'how'"
        >
          How to do it
        </TabPill>
      </div>
      <div
        class="tw-p-8 tw-pt-0 tw-prose tw-max-w-none"
        :class="{'tw-pt-8': isManualAction}"
        v-html="content"
      />

      <div
        v-if="action.input_label"
        class="tw-p-8 tw-pt-0 tw-relative"
      >
        <!-- This is strictly for url inputs, if anything new is added remove validation schema -->
        <DefaultUrlInput
          ref="inputComponent"
          v-model="inputValue"
          :placeholder="action.input_placeholder"
          :validation-schema="fullUrl"
          :label="action.input_label"
        />
      </div>

      <div class="tw-p-8 tw-flex tw-gap-4 tw-justify-end tw-border-t">
        <HideActions :action="action" />
        <GenerateFixButton
          v-if="action.can_generate_fix && isAdmin"
          :action="action"
        />
        <CompleteActionButton
          v-model="inputValue"
          :action="action"
          :disabled="disableComplete"
          @show-error="(errorText) => inputComponent.setErrors(errorText ? errorText : 'Please enter a valid URL')"
        />
      </div>
    </div>
  </AccordionTransition>
</template>
